<template>
  <div class="gallery-mini">
    <div class="gallery-mini__image-wrapper">
      <img
        v-if="currentImage"
        :id="idLabel"
        :src="currentImage"
        alt=""
        class="gallery-mini__image"
        :onerror="onImageError"
        onload="this.style.opacity=1"
        loading="lazy"
      >
    </div>
    <button
      v-if="shouldDisplayControls"
      class="gallery-mini__next-button gallery-mini__next-button--left next-button"
      @click.stop.prevent="nextImage(-1)"
    >
      <ArrowIcon />
    </button>

    <div class="gallery-mini__progress">
      <ProgressIndicator
        v-if="shouldDisplayControls"
        :amount="images.length"
        :selected-item="currentImgIndex"
      />
    </div>
    <button
      v-if="shouldDisplayControls"
      class="gallery-mini__next-button gallery-mini__next-button--right next-button"
      @click.stop.prevent="nextImage(1)"
    >
      <ArrowIcon direction="right" />
    </button>
  </div>
</template>

<script lang="ts">
import ArrowIcon from '~/core/components/shared/icons/Arrow.icon.vue'
import ProgressIndicator from './ProgressIndicator.vue'

import { useSwiper } from '~/core/composable/shared/useSwipper'

import type { Picture } from '~/core/ts/entity/Picture'

export default defineNuxtComponent({
  components: {
    ProgressIndicator,
    ArrowIcon,
  },
  props: {
    uuid: {
      type: String,
      required: true,
    },
    images: {
      type: Array as PropType<Array<Picture>>,
      default: () => [],
    },
    displayControls: {
      type: Boolean,
      default: true,
    },
  },
  setup(props) {
    const currentImgIndex = ref(0)
    const updateCurrentImgIndex = (value: number) => currentImgIndex.value = value

    const pictures = ref(props.images)

    const currentImage = computed(() => {
      if (!pictures.value.length) {
        return 'https://marketplace-assets.s3.eu-west-3.amazonaws.com/2020/10/45656-pre-barrosapalace-spa4206.jpg'
      }

      return pictures.value[currentImgIndex.value].url
    })

    const nextImage = (direction: number) => {
      let newIndex = currentImgIndex.value + direction
      const lastIndex = pictures.value.length - 1

      if (newIndex > lastIndex) {
        newIndex = 0
      }

      if (newIndex < 0) {
        newIndex = lastIndex
      }

      updateCurrentImgIndex(newIndex)
    }

    const idLabel = computed(() => props.uuid)

    const shouldDisplayControls = computed(() => {
      if (!props.displayControls) {
        return false
      }

      return pictures.value.length > 1
    })

    useSwiper(idLabel.value, {
      handleSwipeLeft: () => nextImage(-1),
      handleSwipeRight: () => nextImage(1),
    })

    function onImageError() {
      const newPictures = []
      for (let i = 0; i < pictures.value.length; i++) {
        if (i != currentImgIndex.value) {
          newPictures.push(pictures.value[i])
        }
      }

      pictures.value = newPictures
    }

    return {
      idLabel,
      currentImage,
      currentImgIndex,

      shouldDisplayControls,

      nextImage,
      onImageError,
    }
  },
})
</script>

<style lang="scss" scoped>
.gallery-mini {
  @apply h-full;
  @apply rounded-4xl;

  &__image-wrapper {
    @apply relative;

    @apply overflow-hidden;
    @apply h-full w-full;
    @apply rounded-4xl;
  }

  &__image {
    @apply w-full h-full;

    @apply absolute;
    @apply object-cover;
  }

  &__progress {
    @apply -mt-8;
    @apply hidden;

    @screen lg {
      @apply flex justify-center;
    }
  }

  &__next-button {
    z-index: 2;

    @apply absolute;
    top: calc(50% - 20px);

    &--left {
      @apply left-6;
    }
    &--right {
      @apply right-6;
    }
  }
}

.next-button {
  @apply bg-spl-gray-1;
  @apply opacity-70;
  @apply rounded-full;

  @apply h-11;
  @apply w-11;
  transition: 0.25s;
  &:active {
    @apply bg-spl-secondary-dark;
  }

  @apply hidden;
  @screen lg {
    // Para colocar la flecha en el centro.
    @apply flex justify-center items-center;
  }
}
</style>
